import { useState, FormEvent } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import SEOHead from "../../components/feature/SEOHead";
import JsonLd from "../../components/feature/JsonLd";

const FORM_URL = "https://readdy.ai/api/form/d72qvdsmc4tbhfnc6k4g";

const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) ?? "";

const applySchema: Record<string, unknown>[] = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Apply to The Magazine Co. | Turn Your Content Into Print",
    url: `${siteUrl}/apply`,
    description:
      "Apply to The Magazine Co. and turn your creator content into a beautifully crafted physical magazine. Zero upfront cost, revenue share model, response within 48 hours.",
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: siteUrl },
        { "@type": "ListItem", position: 2, name: "Apply", item: `${siteUrl}/apply` },
      ],
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Creator Magazine Publishing Service",
    provider: {
      "@type": "Organization",
      name: "The Magazine Co.",
      url: siteUrl,
    },
    url: `${siteUrl}/apply`,
    description:
      "The Magazine Co. transforms independent creator content into premium physical print magazines. We handle editorial design, printing, and global distribution — you keep the creative control.",
    serviceType: "Print Magazine Publishing",
    areaServed: "Worldwide",
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Magazine Distribution Plans",
      itemListElement: [
        { "@type": "Offer", name: "Weekly Distribution", description: "High-volume weekly print issue for engaged creator audiences." },
        { "@type": "Offer", name: "Monthly Distribution", description: "Consistent, curated monthly print magazine." },
        { "@type": "Offer", name: "Quarterly Distribution", description: "Deep, seasonal storytelling in a quarterly print format." },
      ],
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How to Launch Your Print Magazine with The Magazine Co.",
    description: "Apply and launch your own physical print magazine through The Magazine Co. in three simple steps.",
    step: [
      {
        "@type": "HowToStep",
        position: 1,
        name: "We Actually Read It",
        text: "Our team personally reads every single application. No bots, no form-letter rejections. Real humans, real feedback.",
      },
      {
        "@type": "HowToStep",
        position: 2,
        name: "We Chat",
        text: "If it's a good fit, we hop on a quick 30-minute call to talk about your vision. Casual, low-pressure, and actually exciting.",
      },
      {
        "@type": "HowToStep",
        position: 3,
        name: "Your Magazine Goes Live",
        text: "We handle design, printing, and distribution. You keep creating. Revenue sharing kicks in from issue one.",
      },
    ],
  },
];

const CONTENT_TYPES = ["Written Articles", "Photography", "Illustration & Art", "Mixed Media", "Interviews & Profiles", "Data & Research", "Other"];
const NICHES = ["Lifestyle & Wellness", "Travel & Culture", "Food & Gastronomy", "Fashion & Identity", "Architecture & Design", "Technology & Society", "Finance & Business", "Sports & Outdoors", "Art & Music", "Parenting & Family", "Environment & Sustainability", "Other"];
const PLATFORMS = ["Personal Blog / Website", "Substack", "Instagram", "YouTube", "TikTok", "Podcast", "LinkedIn", "Newsletter", "Other"];

const STEPS = [
  { icon: "ri-file-text-line", title: "We Actually Read It", desc: "Our team personally reads every single application. No bots, no form-letter rejections. Real humans, real feedback." },
  { icon: "ri-phone-line", title: "We Chat (It's Fun, Promise)", desc: "If it's a good fit, we hop on a quick 30-minute call to talk about your vision. Casual, low-pressure, actually exciting." },
  { icon: "ri-newspaper-line", title: "Your Magazine Goes Live", desc: "We handle design, printing, and distribution. You just keep doing what you love. Revenue sharing kicks in from issue one." },
];

export default function Apply() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [contentTypes, setContentTypes] = useState<string[]>([]);
  const [bioCount, setBioCount] = useState(0);
  const [visionCount, setVisionCount] = useState(0);

  const toggleContentType = (type: string) => {
    setContentTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const bio = data.get("creator_bio") as string;
    const vision = data.get("magazine_vision") as string;
    if ((bio && bio.length > 500) || (vision && vision.length > 500)) return;

    setStatus("loading");

    const params = new URLSearchParams();
    data.forEach((value, key) => {
      params.append(key, value as string);
    });
    if (contentTypes.length > 0) {
      params.append("content_types", contentTypes.join(", "));
    }

    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      if (res.ok) {
        setStatus("success");
        form.reset();
        setContentTypes([]);
        setBioCount(0);
        setVisionCount(0);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Apply to The Magazine Co. | Turn Your Content Into a Print Magazine"
        description="Apply to The Magazine Co. and launch your own physical magazine. Zero upfront cost, revenue share model, full design and distribution support. We reply within 48 hours."
        keywords="apply magazine publisher, launch print magazine, creator magazine application, self-publish magazine"
        path="/apply"
      />
      <JsonLd id="apply-schema" schema={applySchema} />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-44 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/aa16ba9571fa7a838baa91dc193b1ec3.jpg"
            alt="Creator applying"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-black/60" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 text-center">
          <p className="text-[#F0EAD6] text-xs tracking-[0.3em] uppercase font-semibold mb-5">You Make It. We Print It.</p>
          <h1 className="text-5xl md:text-6xl font-serif font-light text-white leading-[1.1] mb-6">
            Your Magazine<br /><em className="italic">Starts Here</em>
          </h1>
          <p className="text-white/75 text-lg font-light max-w-xl mx-auto leading-relaxed">
            Tell us a bit about yourself and what you create. It takes about 5 minutes and we promise not to ghost you — we get back to every application within 48 hours.
          </p>
        </div>
      </section>

      {/* How It Works — 3 Steps */}
      <section className="bg-[#F8F4EE] py-16 border-b border-[#E8E0D0]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {STEPS.map((step, i) => (
              <div key={step.title} className="flex items-start gap-5">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#C41E3A] flex items-center justify-center">
                  <span className="text-white text-lg w-6 h-6 flex items-center justify-center">
                    <i className={step.icon} />
                  </span>
                </div>
                <div>
                  <p className="text-[#C41E3A] text-xs tracking-widest uppercase font-semibold mb-1">Step {i + 1}</p>
                  <h3 className="font-serif text-lg text-[#2B2B2B] mb-1.5">{step.title}</h3>
                  <p className="text-[#6B6B6B] text-sm leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="bg-[#F8F4EE] py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-5 gap-14">

          {/* Sidebar */}
          <aside className="lg:col-span-2 flex flex-col gap-8">
            <div>
              <p className="text-[#C41E3A] text-xs tracking-[0.3em] uppercase font-semibold mb-5">What Makes a Great Fit</p>
              <ul className="flex flex-col gap-4">
                {[
                  { icon: "ri-quill-pen-line", label: "A Distinctive Voice", desc: "You've got a POV — readers show up because they want to hear it." },
                  { icon: "ri-group-line", label: "Some Kind of Audience", desc: "Big or small doesn't matter. Engaged does. Any community counts." },
                  { icon: "ri-image-line", label: "Content That Pops", desc: "If it looks good on a screen, it'll look incredible on paper." },
                  { icon: "ri-calendar-check-line", label: "You Actually Publish", desc: "Consistency is the magic ingredient. If you post, you qualify." },
                ].map((item) => (
                  <li key={item.label} className="flex items-start gap-4">
                    <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-[#C41E3A] w-4 h-4 flex items-center justify-center text-sm">
                        <i className={item.icon} />
                      </span>
                    </div>
                    <div>
                      <p className="text-[#2B2B2B] text-sm font-semibold">{item.label}</p>
                      <p className="text-[#6B6B6B] text-xs leading-relaxed">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Revenue Share Box */}
            <div className="bg-[#1C1C1C] rounded-2xl p-6 text-white">
              <p className="text-[#F0EAD6] text-xs tracking-widest uppercase font-semibold mb-3">How You Get Paid</p>
              <p className="font-serif text-2xl mb-3">Revenue Share Model</p>
              <p className="text-white/60 text-sm leading-relaxed">
                No upfront fees. No subscriptions. No surprises. You earn a percentage of every magazine sold — we only make money when you do. That&apos;s the deal.
              </p>
            </div>

            {/* Review Time */}
            <div className="bg-white rounded-2xl p-6 border border-[#E8E0D0]">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F8F4EE]">
                  <span className="text-[#C41E3A] w-5 h-5 flex items-center justify-center">
                    <i className="ri-time-line" />
                  </span>
                </div>
                <p className="text-[#C41E3A] text-xs tracking-widest uppercase font-semibold">We Won&apos;t Ghost You</p>
              </div>
              <p className="font-serif text-2xl text-[#2B2B2B] mb-1.5">Response Within 48 Hours</p>
              <p className="text-[#6B6B6B] text-sm leading-relaxed">
                Real humans, real replies. Every application gets a personal response — no auto-rejections, ever.
              </p>
            </div>

            <p className="text-[#8B8B8B] text-xs leading-relaxed">
              Already applied and haven&apos;t heard back?{" "}
              <Link to="/contact" className="text-[#C41E3A] hover:underline cursor-pointer font-medium">
                Contact us directly
              </Link>
            </p>
          </aside>

          {/* Form */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl p-8 md:p-10">
              {status === "success" ? (
                <div className="flex flex-col items-center justify-center py-20 text-center gap-5">
                  <div className="w-20 h-20 rounded-full bg-[#FFECEF] flex items-center justify-center">
                    <span className="text-[#C41E3A] text-3xl w-10 h-10 flex items-center justify-center">
                      <i className="ri-mail-check-line" />
                    </span>
                  </div>
                  <h2 className="font-serif text-3xl text-[#2B2B2B]">You&apos;re In the Queue!</h2>
                  <p className="text-[#6B6B6B] text-sm max-w-sm leading-relaxed">
                    Nice one! We&apos;ve got your application and our team will be in touch within 48 hours. Check your inbox (and maybe your spam, just in case).
                  </p>
                  <button
                    onClick={() => setStatus("idle")}
                    className="mt-2 text-sm text-[#C41E3A] font-semibold hover:underline cursor-pointer"
                  >
                    Submit another application
                  </button>
                </div>
              ) : (
                <form
                  id="creator-application-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-6"
                >
                  {/* Section: About You */}
                  <div>
                    <h2 className="font-serif text-2xl text-[#2B2B2B] mb-1">First, Who Are You?</h2>
                    <p className="text-[#8B8B8B] text-sm mb-5">Just the basics — name, email, and where we can find your work.</p>
                    <div className="flex flex-col gap-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="flex flex-col gap-1.5">
                          <label className="text-xs tracking-widest uppercase text-[#8B8B8B] font-semibold" htmlFor="first_name">
                            First Name <span className="text-[#C41E3A]">*</span>
                          </label>
                          <input
                            id="first_name" name="first_name" type="text" required placeholder="Jordan"
                            className="w-full px-4 py-3 rounded-lg border border-[#E8E0D0] bg-[#FAFAF8] text-sm text-[#2B2B2B] placeholder:text-[#C0B8A8] focus:outline-none focus:border-[#C41E3A] transition-colors"
                          />
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <label className="text-xs tracking-widest uppercase text-[#8B8B8B] font-semibold" htmlFor="last_name">
                            Last Name <span className="text-[#C41E3A]">*</span>
                          </label>
                          <input
                            id="last_name" name="last_name" type="text" required placeholder="Rivera"
                            className="w-full px-4 py-3 rounded-lg border border-[#E8E0D0] bg-[#FAFAF8] text-sm text-[#2B2B2B] placeholder:text-[#C0B8A8] focus:outline-none focus:border-[#C41E3A] transition-colors"
                          />
                        </div>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs tracking-widest uppercase text-[#8B8B8B] font-semibold" htmlFor="email">
                          Email Address <span className="text-[#C41E3A]">*</span>
                        </label>
                        <input
                          id="email" name="email" type="email" required placeholder="you@example.com"
                          className="w-full px-4 py-3 rounded-lg border border-[#E8E0D0] bg-[#FAFAF8] text-sm text-[#2B2B2B] placeholder:text-[#C0B8A8] focus:outline-none focus:border-[#C41E3A] transition-colors"
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs tracking-widest uppercase text-[#8B8B8B] font-semibold" htmlFor="website">
                          Website or Main Social Profile
                        </label>
                        <input
                          id="website" name="website" type="url" placeholder="https://yoursite.com or @yourhandle"
                          className="w-full px-4 py-3 rounded-lg border border-[#E8E0D0] bg-[#FAFAF8] text-sm text-[#2B2B2B] placeholder:text-[#C0B8A8] focus:outline-none focus:border-[#C41E3A] transition-colors"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="h-px bg-[#E8E0D0]" />

                  {/* Section: Your Content */}
                  <div>
                    <h2 className="font-serif text-2xl text-[#2B2B2B] mb-1">What Do You Create?</h2>
                    <p className="text-[#8B8B8B] text-sm mb-5">Tell us what you make and where the world gets to see it.</p>
                    <div className="flex flex-col gap-4">
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs tracking-widest uppercase text-[#8B8B8B] font-semibold" htmlFor="content_niche">
                          Content Niche <span className="text-[#C41E3A]">*</span>
                        </label>
                        <select
                          id="content_niche" name="content_niche" required
                          className="w-full px-4 py-3 rounded-lg border border-[#E8E0D0] bg-[#FAFAF8] text-sm text-[#2B2B2B] focus:outline-none focus:border-[#C41E3A] transition-colors cursor-pointer"
                        >
                          <option value="">Select your niche</option>
                          {NICHES.map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                      </div>

                      <div className="flex flex-col gap-2">
                        <label className="text-xs tracking-widest uppercase text-[#8B8B8B] font-semibold">
                          Content Type <span className="text-[#C41E3A]">*</span>{" "}
                          <span className="text-[#C0B8A8] normal-case tracking-normal font-normal">(select all that apply)</span>
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {CONTENT_TYPES.map((type) => (
                            <button
                              key={type}
                              type="button"
                              onClick={() => toggleContentType(type)}
                              className={`px-4 py-2 rounded-full text-xs font-semibold tracking-wide whitespace-nowrap transition-all cursor-pointer border ${
                                contentTypes.includes(type)
                                  ? "bg-[#C41E3A] text-white border-[#C41E3A]"
                                  : "bg-white text-[#6B6B6B] border-[#E8E0D0] hover:border-[#C41E3A] hover:text-[#C41E3A]"
                              }`}
                            >
                              {type}
                            </button>
                          ))}
                        </div>
                        {contentTypes.length === 0 && (
                          <p className="text-[#C0B8A8] text-xs">Please select at least one content type.</p>
                        )}
                      </div>

                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs tracking-widest uppercase text-[#8B8B8B] font-semibold" htmlFor="primary_platform">
                          Primary Publishing Platform <span className="text-[#C41E3A]">*</span>
                        </label>
                        <select
                          id="primary_platform" name="primary_platform" required
                          className="w-full px-4 py-3 rounded-lg border border-[#E8E0D0] bg-[#FAFAF8] text-sm text-[#2B2B2B] focus:outline-none focus:border-[#C41E3A] transition-colors cursor-pointer"
                        >
                          <option value="">Where do you mainly publish?</option>
                          {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </div>

                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs tracking-widest uppercase text-[#8B8B8B] font-semibold" htmlFor="audience_size">
                          Estimated Audience Size <span className="text-[#C41E3A]">*</span>
                        </label>
                        <select
                          id="audience_size" name="audience_size" required
                          className="w-full px-4 py-3 rounded-lg border border-[#E8E0D0] bg-[#FAFAF8] text-sm text-[#2B2B2B] focus:outline-none focus:border-[#C41E3A] transition-colors cursor-pointer"
                        >
                          <option value="">Select a range</option>
                          <option value="Under 1,000">Under 1,000</option>
                          <option value="1,000 – 5,000">1,000 – 5,000</option>
                          <option value="5,000 – 20,000">5,000 – 20,000</option>
                          <option value="20,000 – 100,000">20,000 – 100,000</option>
                          <option value="100,000+">100,000+</option>
                        </select>
                      </div>

                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs tracking-widest uppercase text-[#8B8B8B] font-semibold" htmlFor="portfolio_link">
                          Portfolio or Content Sample Link
                        </label>
                        <input
                          id="portfolio_link" name="portfolio_link" type="url" placeholder="Link to a recent article, post, or portfolio"
                          className="w-full px-4 py-3 rounded-lg border border-[#E8E0D0] bg-[#FAFAF8] text-sm text-[#2B2B2B] placeholder:text-[#C0B8A8] focus:outline-none focus:border-[#C41E3A] transition-colors"
                        />
                      </div>

                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs tracking-widest uppercase text-[#8B8B8B] font-semibold" htmlFor="creator_bio">
                          Tell Us About Yourself &amp; Your Content <span className="text-[#C41E3A]">*</span>
                        </label>
                        <textarea
                          id="creator_bio" name="creator_bio" required rows={4} maxLength={500}
                          placeholder="Describe what you create, your creative process, and why your audience follows you..."
                          onChange={(e) => setBioCount(e.target.value.length)}
                          className="w-full px-4 py-3 rounded-lg border border-[#E8E0D0] bg-[#FAFAF8] text-sm text-[#2B2B2B] placeholder:text-[#C0B8A8] focus:outline-none focus:border-[#C41E3A] transition-colors resize-none"
                        />
                        <p className={`text-xs self-end ${bioCount > 480 ? "text-[#C41E3A]" : "text-[#C0B8A8]"}`}>{bioCount}/500</p>
                      </div>
                    </div>
                  </div>

                  <div className="h-px bg-[#E8E0D0]" />

                  {/* Section: Your Magazine */}
                  <div>
                    <h2 className="font-serif text-2xl text-[#2B2B2B] mb-1">Dream It Up With Us</h2>
                    <p className="text-[#8B8B8B] text-sm mb-5">What does your dream magazine look and feel like? No wrong answers here.</p>
                    <div className="flex flex-col gap-4">
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs tracking-widest uppercase text-[#8B8B8B] font-semibold" htmlFor="magazine_title">
                          Proposed Magazine Title
                        </label>
                        <input
                          id="magazine_title" name="magazine_title" type="text" placeholder="What would you name your magazine?"
                          className="w-full px-4 py-3 rounded-lg border border-[#E8E0D0] bg-[#FAFAF8] text-sm text-[#2B2B2B] placeholder:text-[#C0B8A8] focus:outline-none focus:border-[#C41E3A] transition-colors"
                        />
                      </div>

                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs tracking-widest uppercase text-[#8B8B8B] font-semibold" htmlFor="distribution_preference">
                          Preferred Distribution Frequency <span className="text-[#C41E3A]">*</span>
                        </label>
                        <select
                          id="distribution_preference" name="distribution_preference" required
                          className="w-full px-4 py-3 rounded-lg border border-[#E8E0D0] bg-[#FAFAF8] text-sm text-[#2B2B2B] focus:outline-none focus:border-[#C41E3A] transition-colors cursor-pointer"
                        >
                          <option value="">Select a cadence</option>
                          <option value="Weekly">Weekly — High-volume, engaged audiences</option>
                          <option value="Monthly">Monthly — Consistent, curated content</option>
                          <option value="Quarterly">Quarterly — Deep, seasonal storytelling</option>
                          <option value="Not sure yet">Not sure yet — Help me decide</option>
                        </select>
                      </div>

                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs tracking-widest uppercase text-[#8B8B8B] font-semibold" htmlFor="magazine_vision">
                          Describe Your Magazine Vision <span className="text-[#C41E3A]">*</span>
                        </label>
                        <textarea
                          id="magazine_vision" name="magazine_vision" required rows={4} maxLength={500}
                          placeholder="What does your ideal magazine look, feel, and read like? Who is it for? What is its purpose?"
                          onChange={(e) => setVisionCount(e.target.value.length)}
                          className="w-full px-4 py-3 rounded-lg border border-[#E8E0D0] bg-[#FAFAF8] text-sm text-[#2B2B2B] placeholder:text-[#C0B8A8] focus:outline-none focus:border-[#C41E3A] transition-colors resize-none"
                        />
                        <p className={`text-xs self-end ${visionCount > 480 ? "text-[#C41E3A]" : "text-[#C0B8A8]"}`}>{visionCount}/500</p>
                      </div>
                    </div>
                  </div>

                  {status === "error" && (
                    <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm">
                      Something went wrong. Please try again or{" "}
                      <Link to="/contact" className="underline cursor-pointer">contact us directly</Link>.
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === "loading" || contentTypes.length === 0}
                    className="w-full py-4 rounded-full bg-[#C41E3A] text-white text-sm font-semibold tracking-wide whitespace-nowrap hover:bg-[#A0182E] transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {status === "loading" ? (
                      <>
                        <span className="w-4 h-4 flex items-center justify-center animate-spin"><i className="ri-loader-4-line" /></span>
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit My Application — Let&apos;s Go
                        <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line" /></span>
                      </>
                    )}
                  </button>

                  <p className="text-center text-[#C0B8A8] text-xs leading-relaxed">
                    By applying, you agree to be contacted by our editorial team. We respect your privacy and never share your info.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
